import { WtApplication } from '@webitel/ui-sdk/enums';
import { createRouter, createWebHistory } from 'vue-router';

import AgentRoutes from '../../modules/contact-center/modules/agents/router/agents';
import QueuesRoutes from '../../modules/contact-center/modules/queues/router/queues';
import ResourcesGroupRoutes from '../../modules/contact-center/modules/resource-groups/router/resourceGroup';
import ResourcesRoutes from '../../modules/contact-center/modules/resources/router/resources';
import TeamsRoutes from '../../modules/contact-center/modules/teams/router/teams';
import DevicesRoutes from '../../modules/directory/modules/devices/router/devices';
import LicenseRoutes from '../../modules/directory/modules/license/router/license';
import UsersRoutes from '../../modules/directory/modules/users/routes/routes.js';
import CognitiveProfilesRoutes from '../../modules/integrations/modules/cognitive-profiles/router/cognitiveProfiles';
import EmailProfilesRoutes from '../../modules/integrations/modules/email-profiles/router/emailProfiles';
import ImportCsvRoutes from '../../modules/integrations/modules/import-csv/router/importCsv';
import SingleSignOnRoutes from '../../modules/integrations/modules/single-sign-on/router/singleSignOn';
import StorageRoutes from '../../modules/integrations/modules/storage/router/storage';
import StoragePoliciesRoutes from '../../modules/integrations/modules/storage-policies/router/storage-policies';
import TriggersRoutes from '../../modules/integrations/modules/triggers/router/triggers';
import ActivityTypeRoutes from '../../modules/lookups/modules/activity-types/router/activityTypes.js';
import AgentPauseCauseRoutes from '../../modules/lookups/modules/agent-pause-cause/router/agentPauseCause';
import AgentSkillsRoutes from '../../modules/lookups/modules/agent-skills/router/agentSkills';
import BlacklistsRoutes from '../../modules/lookups/modules/blacklists/router/blacklists';
import BucketsRoutes from '../../modules/lookups/modules/buckets/router/buckets';
import CalendarsRoutes from '../../modules/lookups/modules/calendars/router/calendars';
import CommunicationsRoutes from '../../modules/lookups/modules/communications/router/communications';
import MediaRoutes from '../../modules/lookups/modules/media/router/media';
import QuickRepliesRoutes from '../../modules/lookups/modules/quick-replies/router/quickReplies.js';
import RegionsRoutes from '../../modules/lookups/modules/regions/router/regions';
import ObjectsRoutes from '../../modules/permissions/modules/objects/router/objects';
import RolesRoutes from '../../modules/permissions/modules/roles/router/roles';
import ChatGatewaysRoutes from '../../modules/routing/modules/chat-gateways/router/chatGateways';
import ChatplanRoutes from '../../modules/routing/modules/chatplan/router/chatplan';
import DialplanRoutes from '../../modules/routing/modules/dialplan/router/dialplan';
import FlowRoutes from '../../modules/routing/modules/flow/router/flow';
import GatewaysRoutes from '../../modules/routing/modules/gateways/router/gateways';
import SettingsRoutes from '../../modules/settings/routes/routes.js';
import ChangeLogsRoutes from '../../modules/system/modules/changelogs/router/changelogs';
import ConfigurationRoutes from '../../modules/system/modules/configuration/router/configuration';
import GlobalVariablesRoutes from '../../modules/system/modules/global-variables/router/globalVariables';
import RouteNames from './_internals/RouteNames.enum.js';
import RoutePaths from './_internals/RoutePaths';
import { createAuthGuard } from './authGuard';

const ApplicationHub = () =>
	import('../../modules/application-hub/components/application-hub.vue');
const ModuleWrap = () =>
	import('../../modules/_shared/object-wrap/the-object-wrap.vue');
const StartPage = () =>
	import('../../modules/start-page/components/the-start-page.vue');
const AccessDenied = () =>
	import(
		'../../modules/error-pages/components/the-access-denied-component.vue'
	);
const NotFound = () =>
	import('../../modules/error-pages/components/the-not-found-component.vue');

export let router = null;

export const initRouter = async ({
	beforeEach = [],
	onUnauthorized = () => {},
} = {}) => {
	router = createRouter({
		history: createWebHistory(import.meta.env.BASE_URL),
		scrollBehavior(/*to, from, savedPosition*/) {
			return {
				left: 0,
				top: 0,
			};
		},
		routes: [
			{
				path: '/',
				name: RouteNames.APPLICATION_HUB,
				component: ApplicationHub,
			},
			{
				path: '/access-denied',
				name: RouteNames.PAGE_403,
				component: AccessDenied,
			},
			{
				path: '/admin',
				component: ModuleWrap,
				redirect: RoutePaths.StartPage,
				meta: {
					WtApplication: WtApplication.Admin,
				},
				children: [
					{
						path: RoutePaths.StartPage,
						name: RouteNames.START,
						component: StartPage,
					},
					...SettingsRoutes,
					{
						path: '/:pathMatch(.*)*',
						name: RouteNames.PAGE_404,
						component: NotFound,
					},
					{
						path: '/404',
						name: RouteNames.PAGE_404,
						component: NotFound,
					},

					// ----------DIRECTORY------------
					...DevicesRoutes,
					...LicenseRoutes,
					...UsersRoutes,
					// ----------DIRECTORY END------------

					// ----------ROUTING------------
					...FlowRoutes,
					...ChatGatewaysRoutes,
					...DialplanRoutes,
					...GatewaysRoutes,
					...ChatplanRoutes,
					// ----------ROUTING END------------

					// ----------LOOKUPS------------
					...AgentSkillsRoutes,
					...BucketsRoutes,
					...BlacklistsRoutes,
					...MediaRoutes,
					...CalendarsRoutes,
					...CommunicationsRoutes,
					...RegionsRoutes,
					...AgentPauseCauseRoutes,
					...ActivityTypeRoutes,
					...QuickRepliesRoutes,
					// ----------LOOKUPS END------------

					// --------------CONTACT CENTER-------------
					...AgentRoutes,
					...TeamsRoutes,
					...ResourcesRoutes,
					...ResourcesGroupRoutes,
					...QueuesRoutes,
					// --------------CONTACT CENTER END-------------

					// ----------INTEGRATIONS-----------------
					...StorageRoutes,
					...CognitiveProfilesRoutes,
					...EmailProfilesRoutes,
					...ImportCsvRoutes,
					...TriggersRoutes,
					...StoragePoliciesRoutes,
					...SingleSignOnRoutes,
					// --------------INTEGRATIONS END-------------

					// ----------PERMISSIONS-----------------
					...RolesRoutes,
					...ObjectsRoutes,
					// ----------PERMISSIONS END-----------------

					// ----------SYSTEM START-----------------
					...ChangeLogsRoutes,
					...ConfigurationRoutes,
					...GlobalVariablesRoutes,
					// ----------SYSTEM END-----------------
				],
			},
		],
	});

	router.beforeEach(
		createAuthGuard({
			onUnauthorized,
		}),
	);

	beforeEach.forEach((guard) => {
		router.beforeEach(guard);
	});

	window.router = router;

	return router;
};
